import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCompanyLabels } from '../context/CompanyContext';
import { fetchQueries, COMPLAINT_MANAGEMENT_ENDPOINT } from '../redux/complaintmanagement/actions';
import { fetchComplaintSummary, fetchStatuses } from '../redux/status/actions';
import { fetchWorkflowSteps } from '../redux/Workflow/actions';
import { changeComplaintStatus } from '../redux/changestatus/actions';
import { fetchComplaintTypeById } from '../redux/complaintType/actions';
import { setId } from '../redux/appDetails/reducer';
import { resetComplaintManagementState } from '../redux/complaintmanagement/reducers';
import { resetStatusState } from '../redux/status/reducers';
import { resetChangeStatusState } from '../redux/changestatus/reducers';
import { getSignedUrl } from '../redux/s3/actions';
import { getFormFields } from '../redux/complaint/actions';
import { collectAttachmentPaths, resolveIsAttachmentField } from '../utils/complaintTable';
import api from '../utils/api';
import {
  buildComplaintColumns,
  buildComplaintTableFields,
  buildCsvContent,
  COMPLAINTS_PAGE_SIZE_OPTIONS,
  DEFAULT_COMPLAINTS_PAGE_SIZE,
  formFieldColumnsFromResponse,
  getFirstAvailableComplaintTypeId,
  getPaginationInfo,
  getNextUpdatedAtSortDirection,
  isAttachmentColumn,
  isResolvedOrClosedStatus,
  mapComplaintToTableRow,
  readStoredComplaintTypeId,
  saveComplaintTypeId,
  sortComplaintRowsByUpdatedAt,
  UPDATED_AT_SORT_NEWEST,
  UPDATED_AT_SORT_OLDEST,
  REPORT_ROW_LIMIT,
  STATUS_FIELD,
  ESCALATION_FIELD,
  unwrapComplaintListResponse,
} from '../utils/complaintManagementDetails';

function getStatusDropdownOptions(statusList, row) {
  return statusList.map((item) => {
    const resolveLike = isResolvedOrClosedStatus(item.name);
    const isCurrent = String(item.id) === String(row.statusId);
    let disabled = false;
    if (resolveLike && !row.canResolve) disabled = true;
    if (row.isFinalLevel && !resolveLike) disabled = true;
    if (isCurrent) disabled = false;
    return { value: item.id, label: item.name, disabled };
  }).filter((item) => item.label);
}

// --- Escalation level restriction removed ---
// Previously only the current level or the immediate next level could be
// selected. Now every configured level is selectable at any time, in any
// direction (forward or backward).
function getEscalationDropdownOptions(steps) {
  const sorted = [...steps].sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0));
  return sorted.map((step) => ({
    value: step.id,
    label: step.levelNo != null ? `Level ${step.levelNo}` : String(step.name || step.id),
    disabled: false,
  })).filter((item) => item.label);
}

async function fetchAllComplaintsForReport({ complaintTypeId, status }) {
  const response = await api.get(COMPLAINT_MANAGEMENT_ENDPOINT, {
    params: {
      complaintTypeId,
      page: 1,
      limit: REPORT_ROW_LIMIT,
      ...(status != null && status !== '' && status !== 'all' ? { status } : {}),
    },
  });
  return unwrapComplaintListResponse(response.data);
}

function currentUserRoleIds(currentUser) {
  const roles = Array.isArray(currentUser?.roles) ? currentUser.roles : [];
  return roles
    .map((role) => Number(role?.id ?? role?.roleId ?? role))
    .filter((id) => Number.isFinite(id));
}

function isColumnHiddenForUser(column, workflowSteps = [], userRoleIds = []) {
  const stepId = column?.hideForWorkflowStepId;
  if (stepId == null || stepId === '' || !userRoleIds.length) return false;
  const step = workflowSteps.find((item) => String(item.id) === String(stepId));
  if (!step) return false;
  const stepRoleIds = Array.isArray(step.roleIds) ? step.roleIds.map(Number) : [];
  return stepRoleIds.some((id) => userRoleIds.includes(id));
}

export default function useComplaintManagementDetails() {
  const { fieldLabel } = useCompanyLabels();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { complaintTypeId: routeComplaintTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const storeComplaintTypeId = useSelector((store) => store.appDetails?.id);
  const complaintTypeId = getFirstAvailableComplaintTypeId(
    routeComplaintTypeId,
    searchParams.get('complaintTypeId'),
    storeComplaintTypeId,
    readStoredComplaintTypeId(),
  );
  const {
    list = [],
    isLoading = false,
    error = null,
    hasLoaded = false,
    requestedComplaintTypeId = null,
    meta = null,
  } = useSelector((store) => store.complaintManagement ?? {});
  const statusList = useSelector((store) => store.status?.list ?? []);
  const statusUpdateError = useSelector((store) => store.changeStatus?.error ?? null);
  const updatingId = useSelector((store) => store.changeStatus?.updatingId ?? null);
  const workflowSteps = useSelector((store) => store.workflow?.workflowSteps);
  const currentUser = useSelector((store) => store.appDetails?.details?.currentUser);
  const statusNames = useMemo(() => statusList.map((item) => item.name).filter(Boolean), [statusList]);
  const steps = useMemo(() => (Array.isArray(workflowSteps) ? workflowSteps : []), [workflowSteps]);
  const userRoleIds = useMemo(() => currentUserRoleIds(currentUser), [currentUser]);
  const complaintTypeAllowedRoleIds = useSelector((store) => {
    const selected = store.complaintType?.selected;
    const fromAccesses = Array.isArray(selected?.accesses)
      ? selected.accesses.map((access) => access?.role?.id).filter((id) => id != null)
      : [];
    if (fromAccesses.length) return fromAccesses.map(Number);
    const fromRoleIds = Array.isArray(selected?.roleIds) ? selected.roleIds : [];
    return fromRoleIds.map(Number);
  });
  const isComplaintTypeActive = useSelector((store) => {
    const selected = store.complaintType?.selected;
    if (selected?.isActive != null) return Boolean(selected.isActive);
    if (selected?.active != null) return Boolean(selected.active);
    return true;
  });
  const [statusFilter, setStatusFilter] = useState({ complaintTypeId: null, status: 'all' });
  const [updatedAtSortFilter, setUpdatedAtSortFilter] = useState({ complaintTypeId: null, direction: UPDATED_AT_SORT_NEWEST });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_COMPLAINTS_PAGE_SIZE);
  const [configuredFieldColumns, setConfiguredFieldColumns] = useState([]);
  const [attachmentUrlMap, setAttachmentUrlMap] = useState({});
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [toasts, setToasts] = useState([]);
  const selectedStatus = String(statusFilter.complaintTypeId) === String(complaintTypeId) ? statusFilter.status : 'all';
  const selectedUpdatedAtSort = String(updatedAtSortFilter.complaintTypeId) === String(complaintTypeId)
    ? updatedAtSortFilter.direction
    : UPDATED_AT_SORT_NEWEST;

  const addToast = (message, type = 'success') => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((current) => [...current, { id, message, type }]);
  };
  const dismissToast = (id) => setToasts((current) => current.filter((toast) => toast.id !== id));

  useEffect(() => {
    if (!complaintTypeId || routeComplaintTypeId) return undefined;
    saveComplaintTypeId(complaintTypeId);
    navigate(`/complaintmanagement/${complaintTypeId}`, { replace: true });
    return undefined;
  }, [complaintTypeId, navigate, routeComplaintTypeId]);

  useEffect(() => {
    if (!complaintTypeId || !routeComplaintTypeId) return undefined;
    saveComplaintTypeId(complaintTypeId);
    dispatch(setId(complaintTypeId));
    dispatch(resetComplaintManagementState());
    dispatch(resetStatusState());
    dispatch(resetChangeStatusState());

    const statusesRequest = dispatch(fetchStatuses(complaintTypeId));
    const summaryRequest = dispatch(fetchComplaintSummary(complaintTypeId));
    const workflowRequest = dispatch(fetchWorkflowSteps(complaintTypeId));
    const formFieldsRequest = dispatch(getFormFields(complaintTypeId));
    const complaintTypeRequest = dispatch(fetchComplaintTypeById(complaintTypeId));
    formFieldsRequest.unwrap()
      .then((response) => setConfiguredFieldColumns(formFieldColumnsFromResponse(response)))
      .catch(() => setConfiguredFieldColumns([]));

    return () => {
      statusesRequest.abort?.();
      summaryRequest.abort?.();
      workflowRequest.abort?.();
      formFieldsRequest.abort?.();
      complaintTypeRequest.abort?.();
    };
  }, [complaintTypeId, dispatch, routeComplaintTypeId]);

  useEffect(() => {
    if (!complaintTypeId || !routeComplaintTypeId) return undefined;
    const complaintsRequest = dispatch(fetchQueries({
      complaintTypeId,
      page,
      limit: itemsPerPage,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
    }));
    return () => complaintsRequest.abort?.();
  }, [complaintTypeId, dispatch, itemsPerPage, page, routeComplaintTypeId, selectedStatus]);

  // --- Hide-for-role column filtering ---
  // Hides a column from the Complaint Management table (and CSV export) when
  // the logged-in user's role belongs to the workflow level selected in that
  // field's "Hide for" setting (Form Fields step). Matching is done by numeric
  // ID: a field's hideForWorkflowStepId must equal a workflow step's id, and
  // that step's roleIds must include one of the current user's role ids.
  //
  // To turn OFF this feature completely (show all columns to everyone,
  // regardless of "Hide for" settings), change the line below to:
  //   const isColumnKeyHiddenForUser = () => false;
  const hideForWorkflowStepIdByKey = useMemo(
    () => new Map(configuredFieldColumns.map((col) => [col.key, col.hideForWorkflowStepId])),
    [configuredFieldColumns],
  );

    const isColumnKeyHiddenForUser = useCallback((key) => isColumnHiddenForUser(
    { hideForWorkflowStepId: hideForWorkflowStepIdByKey.get(key) },
    steps,
    userRoleIds,
  ), [hideForWorkflowStepIdByKey, steps, userRoleIds]);

  // --- New Complaint button visibility ---
  // Shows the "New Complaint" button only when the current user's role is
  // one of the roles selected in "Select Roles for Complaint Filling"
  // (Category Type step). If that complaint type has no roles configured
  // (empty list), the button stays visible to everyone (safe default).
  //
  // To turn OFF this restriction completely (always show the button),
  // change the line below to:
  //   const canCreateComplaint = true;
  const canCreateComplaint = complaintTypeAllowedRoleIds.length === 0
    || complaintTypeAllowedRoleIds.some((id) => userRoleIds.includes(id));

  const complaintRows = useMemo(() => {
    if (requestedComplaintTypeId != null && complaintTypeId != null
      && String(requestedComplaintTypeId) !== String(complaintTypeId)) return [];
    return list.map((complaint) => ({
      ...mapComplaintToTableRow(complaint, statusList, steps),
      _fields: complaint?.formField || [],
    }));
  }, [complaintTypeId, list, requestedComplaintTypeId, statusList, steps]);
    const complaintColumns = useMemo(
    () => buildComplaintColumns(complaintRows, configuredFieldColumns)
      .filter((col) => !isColumnKeyHiddenForUser(col.key)),
    [complaintRows, configuredFieldColumns, isColumnKeyHiddenForUser],
  );
  const tableFields = buildComplaintTableFields(complaintColumns);
  const sortedComplaintRows = useMemo(
    () => sortComplaintRowsByUpdatedAt(complaintRows, selectedUpdatedAtSort),
    [complaintRows, selectedUpdatedAtSort],
  );
  const tableRows = sortedComplaintRows.map(({ _fields, ...row }) => row);
  const attachmentPaths = useMemo(
    () => collectAttachmentPaths(complaintColumns, sortedComplaintRows),
    [complaintColumns, sortedComplaintRows],
  );
  const attachmentsReady = attachmentPaths.every((path) => Object.prototype.hasOwnProperty.call(attachmentUrlMap, path));

  useEffect(() => {
    const missingPaths = attachmentPaths.filter((path) => !Object.prototype.hasOwnProperty.call(attachmentUrlMap, path));
    if (!missingPaths.length) return undefined;
    let cancelled = false;
    Promise.all(missingPaths.map(async (path) => {
      try {
        const signedUrl = await dispatch(getSignedUrl({ params: { path } })).unwrap();
        return [path, signedUrl];
      } catch {
        return [path, ''];
      }
    })).then((results) => {
      if (!cancelled) setAttachmentUrlMap((previous) => ({ ...previous, ...Object.fromEntries(results) }));
    });
    return () => { cancelled = true; };
  }, [attachmentPaths, attachmentUrlMap, dispatch]);

  const pagination = getPaginationInfo(meta, page, itemsPerPage, tableRows.length);
  const showLoading = Boolean(complaintTypeId && !error && (
    (!tableRows.length && (isLoading || !hasLoaded)) || (tableRows.length > 0 && !attachmentsReady)
  ));

  const handleStatusFilterChange = (nextStatus) => {
    setPage(1);
    setStatusFilter({ complaintTypeId, status: nextStatus });
    if (!complaintTypeId) return;
    dispatch(fetchQueries({ complaintTypeId, page: 1, limit: itemsPerPage, status: nextStatus === 'all' ? undefined : nextStatus }));
  };
  const handlePageChange = (nextPage) => {
    if (nextPage >= 1 && nextPage <= pagination.totalPages && nextPage !== page) setPage(nextPage);
  };
  const handleItemsPerPageChange = (nextItemsPerPage) => {
    const normalized = Number(nextItemsPerPage);
    if (!Number.isFinite(normalized) || normalized <= 0 || normalized === itemsPerPage) return;
    setItemsPerPage(normalized);
    setPage(1);
  };
  const toggleUpdatedAtSortDirection = () => setUpdatedAtSortFilter({
    complaintTypeId,
    direction: getNextUpdatedAtSortDirection(selectedUpdatedAtSort),
  });
  const handleNewComplaint = () => {
    if (complaintTypeId) navigate(`/complaintmanagement/new/${complaintTypeId}`);
  };

  // --- Task 1: Status/Escalation changes now open a Remark modal ---
  // Selecting a new value no longer calls the API immediately. It stores the
  // intended change in `pendingChange` and waits for the modal's Save/Cancel.
  const handleComplaintStatusChange = (row, nextValue) => {
    if (row?.id == null || (updatingId != null && String(updatingId) === String(row.id))) return;
    if (String(nextValue) === String(row.statusId) || String(nextValue) === String(row.status)) return;
    const status = statusList.find((item) => String(item.id) === String(nextValue) || String(item.name) === String(nextValue));
    if (status?.id == null) return;
    const resolveLike = isResolvedOrClosedStatus(status.name);
    if ((resolveLike && !row.canResolve) || (row.isFinalLevel && !resolveLike)) return;
    setRemarkDraft('');
    setPendingChange({
      type: 'status',
      row,
      statusId: status.id,
      currentStepId: row.currentStepId,
      fromLabel: row.status || '—',
      toLabel: status.name,
    });
  };

  const handleComplaintEscalationChange = (row, nextValue) => {
    if (row?.id == null || (updatingId != null && String(updatingId) === String(row.id))) return;
    if (String(nextValue) === String(row.currentStepId) || String(nextValue) === String(row.escalationTo)) return;
    const nextStep = steps.find((step) => String(step.id) === String(nextValue) || `Level ${step.levelNo}` === String(nextValue));
    if (nextStep?.id == null) return;
    setRemarkDraft('');
    setPendingChange({
      type: 'escalation',
      row,
      statusId: row.statusId,
      currentStepId: nextStep.id,
      fromLabel: row.escalationTo || '—',
      toLabel: nextStep.levelNo != null ? `Level ${nextStep.levelNo}` : String(nextStep.name || nextStep.id),
    });
  };

  const confirmPendingChange = () => {
    if (!pendingChange) return;
    const { row, statusId, currentStepId, type, fromLabel, toLabel } = pendingChange;
    dispatch(changeComplaintStatus({
      id: row.id,
      statusId,
      currentStepId,
      remark: remarkDraft,
      onSuccess: () => {
        addToast(
          type === 'status'
            ? `Successfully changed Status from ${fromLabel} to ${toLabel}`
            : `Successfully changed Escalation from ${fromLabel} to ${toLabel}`,
          'success',
        );
      },
      onFail: (requestError) => {
        const message = requestError?.response?.data?.message ?? requestError?.message ?? 'Failed to update complaint';
        addToast(message, 'error');
      },
    }));
    setPendingChange(null);
    setRemarkDraft('');
  };

  const cancelPendingChange = () => {
    setPendingChange(null);
    setRemarkDraft('');
  };

  const getComplaintCellContext = (row) => ({
    statusOptions: getStatusDropdownOptions(statusList, row),
    escalationOptions: getEscalationDropdownOptions(steps),
    onStatusChange: (nextValue) => handleComplaintStatusChange(row, nextValue),
    onEscalationChange: (nextValue) => handleComplaintEscalationChange(row, nextValue),
    attachmentUrlMap,
  });
  const handleDownloadReport = async () => {
    if (!complaintTypeId || isDownloadingReport) return;
    setIsDownloadingReport(true);
    setDownloadError(null);
    try {
      const rawComplaints = await fetchAllComplaintsForReport({ complaintTypeId, status: selectedStatus });
      const reportRows = rawComplaints.map((complaint) => ({
        ...mapComplaintToTableRow(complaint, statusList, steps),
        _fields: complaint?.formField || [],
      }));
      const reportFormColumns = buildComplaintColumns(reportRows, configuredFieldColumns)
        .filter((col) => !isColumnKeyHiddenForUser(col.key));
      const reportFields = buildComplaintTableFields(reportFormColumns)
        .filter((field) => !isAttachmentColumn(field, reportRows, resolveIsAttachmentField));
      const blob = new Blob([buildCsvContent(reportFields, reportRows)], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `complaints-report-${complaintTypeId}-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadRequestError) {
      setDownloadError(downloadRequestError?.response?.data?.message ?? downloadRequestError?.message ?? 'Failed to download report');
    } finally {
      setIsDownloadingReport(false);
    }
  };

  return {
    complaintTypeId,
    canCreateComplaint,
    isComplaintTypeActive,
    statusNames,
    selectedStatus,
    selectedUpdatedAtSort,
    isDownloadingReport,
    onStatusChange: handleStatusFilterChange,
    onSortChange: toggleUpdatedAtSortDirection,
    onNewComplaint: handleNewComplaint,
    onDownloadReport: handleDownloadReport,
    error,
    downloadError,
    statusUpdateError,
    showLoading,
    pendingChange,
    remarkDraft,
    onRemarkDraftChange: setRemarkDraft,
    onConfirmPendingChange: confirmPendingChange,
    onCancelPendingChange: cancelPendingChange,
    toasts,
    onDismissToast: dismissToast,
    tableProps: {
      scrollFields: tableFields.filter((field) => field.key !== 'status' && field.key !== 'escalationTo'),
      stickyFields: [STATUS_FIELD, ESCALATION_FIELD],
      tableRows,
      fieldLabel,
      getCellContext: getComplaintCellContext,
      page,
      itemsPerPage,
    },
    paginationProps: {
      currentPage: page,
      totalItems: pagination.totalItems,
      itemsPerPage,
      onPageChange: handlePageChange,
      onItemsPerPageChange: handleItemsPerPageChange,
      selectOptions: COMPLAINTS_PAGE_SIZE_OPTIONS,
      totalPages: pagination.totalPages,
      disabled: showLoading,
      hasRows: Boolean(pagination.totalItems > 0 || tableRows.length || page > 1),
    },
  };
}